import { useState } from 'react';
import { ChartBar, Table, ArrowsClockwise, Tooth } from '@phosphor-icons/react';
import ScorecardReport from './ScorecardReport';
import DentalIndicesPanel from './DentalIndicesPanel';

/**
 * Panel principal de Informes del estudio. Muestra una galeria de informes
 * disponibles. Cada informe se abre dentro del propio panel para mantener al
 * usuario en el contexto del estudio.
 */
export default function ReportsPanel({ studyId, studyCode, lang = 'es', isDentalStudy = false }) {
  const [openReport, setOpenReport] = useState(null);

  const lb = lang === 'es'
    ? {
        title: 'Informes',
        subtitle: 'Informes generados al vuelo a partir de los datos del estudio. Los datos no se almacenan: cada vez que abres un informe se calcula con la informacion mas reciente.',
        scorecard_title: 'Scorecard',
        scorecard_desc: 'Tabla pivote con todas las respuestas de los voluntarios incluidos por visita y cuestionario. Dos pestañas: cuestionarios clinicos y de satisfaccion. Exportable a Excel.',
        dental_title: 'Indices Clinicos Odontologia',
        dental_desc: 'Calculo Löe & Silness (PI, GI, PPD, BOP) con separacion Natural vs Implantes por grupos adyacentes. Promedios por visita y desglose por voluntario. Exportable a Excel.',
        open: 'Abrir informe',
        back: 'Volver a Informes',
      }
    : {
        title: 'Reports',
        subtitle: 'Reports generated on demand from the study data. Nothing is stored: every open recalculates from the latest information.',
        scorecard_title: 'Scorecard',
        scorecard_desc: 'Pivot table with all included volunteers responses by visit and questionnaire. Two tabs: clinical questionnaires and satisfaction. Exportable to Excel.',
        dental_title: 'Dental Clinical Indices',
        dental_desc: 'Löe & Silness computation (PI, GI, PPD, BOP) with Natural vs Implant group separation. Averages per visit and volunteer breakdown. Excel export.',
        open: 'Open report',
        back: 'Back to Reports',
      };

  if (openReport === 'scorecard') {
    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => setOpenReport(null)}
          data-testid="reports-back"
          className="text-xs text-stone-500 hover:text-[#1f3e80] inline-flex items-center gap-1"
        >
          ← {lb.back}
        </button>
        <ScorecardReport studyId={studyId} studyCode={studyCode} lang={lang} />
      </div>
    );
  }

  if (openReport === 'dental_indices') {
    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => setOpenReport(null)}
          data-testid="reports-back-dental"
          className="text-xs text-stone-500 hover:text-[#1f3e80] inline-flex items-center gap-1"
        >
          ← {lb.back}
        </button>
        <DentalIndicesPanel studyId={studyId} />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="reports-panel">
      <div>
        <h3 className="text-lg font-semibold text-stone-800 flex items-center gap-2">
          <ChartBar size={18} className="text-[#1f3e80]" />
          {lb.title}
        </h3>
        <p className="text-xs text-stone-500 mt-0.5 max-w-3xl">{lb.subtitle}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Scorecard card */}
        <button
          type="button"
          data-testid="report-card-scorecard"
          onClick={() => setOpenReport('scorecard')}
          className="group text-left bg-white border border-stone-200 rounded-sm p-5 hover:border-[#1f3e80] hover:shadow-sm transition-all"
        >
          <div className="flex items-start justify-between mb-3">
            <div className="w-10 h-10 rounded-sm bg-[#1f3e80]/10 flex items-center justify-center text-[#1f3e80] group-hover:bg-[#1f3e80] group-hover:text-white transition-colors">
              <Table size={20} weight="duotone" />
            </div>
            <ArrowsClockwise size={14} className="text-stone-300 group-hover:text-[#1f3e80] transition-colors" />
          </div>
          <h4 className="text-sm font-semibold text-stone-800 mb-1">{lb.scorecard_title}</h4>
          <p className="text-xs text-stone-500 leading-relaxed">{lb.scorecard_desc}</p>
          <p className="text-[11px] text-[#1f3e80] font-medium mt-3 group-hover:underline">{lb.open} →</p>
        </button>

        {/* URS-F381: Indices Clinicos Odontologia (solo si el estudio es dental) */}
        {isDentalStudy && (
          <button
            type="button"
            data-testid="report-card-dental-indices"
            onClick={() => setOpenReport('dental_indices')}
            className="group text-left bg-white border border-stone-200 rounded-sm p-5 hover:border-[#1f3e80] hover:shadow-sm transition-all"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="w-10 h-10 rounded-sm bg-[#1f3e80]/10 flex items-center justify-center text-[#1f3e80] group-hover:bg-[#1f3e80] group-hover:text-white transition-colors">
                <Tooth size={20} weight="duotone" />
              </div>
              <ArrowsClockwise size={14} className="text-stone-300 group-hover:text-[#1f3e80] transition-colors" />
            </div>
            <h4 className="text-sm font-semibold text-stone-800 mb-1">{lb.dental_title}</h4>
            <p className="text-xs text-stone-500 leading-relaxed">{lb.dental_desc}</p>
            <p className="text-[11px] text-[#1f3e80] font-medium mt-3 group-hover:underline">{lb.open} →</p>
          </button>
        )}
      </div>
    </div>
  );
}
